/**
 * Registering the service worker, and the one interaction it needs from the pilot.
 *
 * The worker itself is generated at build time — see `tools/serviceWorkerPlugin.ts` for what it
 * caches and, more importantly, what it deliberately leaves alone. This file is the page's half:
 * register it, notice when a newer build has downloaded, and offer a reload rather than performing
 * one.
 *
 * ## Why the update is offered and never taken
 *
 * A service worker that calls `skipWaiting()` on its own swaps which build serves the next chunk
 * request. The page running right now has a module graph full of content-hashed imports from the
 * *old* build, and the new worker's cache does not contain them — so the next lazily-loaded chunk
 * 404s, in the middle of whatever the pilot was doing. On a flight simulator that is somebody's
 * approach.
 *
 * So the new worker waits. When the pilot chooses to reload, `SKIP_WAITING` goes across, the new
 * worker activates, `controllerchange` fires, and the page reloads once into a consistent build.
 *
 * ## Why not in development
 *
 * A worker registered against the dev server caches unbundled modules and then serves them back
 * over HMR's head, which presents as "my edit did nothing" — repeatedly, and only for whoever last
 * loaded the production build on the same origin. `npm run build && npm run preview` is where this
 * path is exercised, and the README says so.
 *
 * `updateFixed` / `updateFrame`: neither. Registration is a start-up promise and updates arrive
 * as events.
 */

import { PRODUCT_NAME } from '../brand.js';
import { Emitter } from './events.js';

export interface AppUpdateEvents extends Record<string, unknown> {
  /** A newer build is downloaded and waiting. `apply` reloads into it. */
  updateReady: { apply: () => void };
}

export interface RegisterOptions {
  /** Where the generated worker is served. Its scope is the directory it sits in. */
  url?: string;
  /**
   * Register at all? False in development and in every scripted run.
   *
   * A perf run must not be served from a cache: the measurement is of a cold-ish production load,
   * and a worker that has been warmed by a previous run would make the first seconds of the next
   * one unrepresentative in a way nothing in the report would reveal.
   */
  enabled?: boolean;
}

export class AppUpdates {
  readonly events = new Emitter<AppUpdateEvents>();

  private registration: ServiceWorkerRegistration | null = null;
  /** Set once a reload is under way, so `controllerchange` cannot fire two of them. */
  private reloading = false;

  /**
   * Register, and start watching for a newer build.
   *
   * Never throws and never rejects: a browser with service workers disabled, a private window, or
   * an insecure origin all mean "no offline support", which is a missing nicety and not an error.
   * The simulator has to open regardless.
   */
  async register(options: RegisterOptions = {}): Promise<ServiceWorkerRegistration | null> {
    if (options.enabled === false) return null;
    const container = globalThis.navigator?.serviceWorker;
    if (container === undefined) return null;

    try {
      const registration = await container.register(options.url ?? '/sw.js', { scope: '/' });
      this.registration = registration;

      // Already waiting when we registered: the pilot reloaded into the old build while a new one
      // was sitting there from a previous visit. Offer it immediately.
      if (registration.waiting !== null && container.controller !== null) this.offer(registration);

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (installing === null) return;
        installing.addEventListener('statechange', () => {
          /*
           * `controller !== null` is the whole test for "this is an update".
           *
           * On a *first* visit the worker also reaches `installed`, and there is nothing to
           * reload into — the page is already the build it just cached. Without this check every
           * new visitor is greeted by an update prompt seconds after arriving.
           */
          if (installing.state === 'installed' && container.controller !== null) {
            this.offer(registration);
          }
        });
      });

      container.addEventListener('controllerchange', () => {
        if (this.reloading) return;
        this.reloading = true;
        globalThis.location.reload();
      });

      return registration;
    } catch (error) {
      console.warn(`${PRODUCT_NAME}: the service worker did not register —`, error);
      return null;
    }
  }

  /** Is a newer build downloaded and waiting? */
  get hasUpdate(): boolean {
    return this.registration?.waiting !== null && this.registration?.waiting !== undefined;
  }

  private offer(registration: ServiceWorkerRegistration): void {
    this.events.emit('updateReady', {
      apply: () => {
        const waiting = registration.waiting;
        if (waiting === null) {
          // Nothing waiting any more — another tab already took the update. A plain reload lands
          // on the new build, which is what was asked for.
          globalThis.location.reload();
          return;
        }
        // The reload happens on `controllerchange`, above, not here: activating is asynchronous
        // and reloading now would land back on the old worker.
        waiting.postMessage({ type: 'SKIP_WAITING' });
      },
    });
  }
}
